import { ChartModule } from 'primeng/chart';
import { CommonModule, DatePipe } from '@angular/common';
import { Component, computed, effect, HostBinding, Inject, inject, Input, input, signal, untracked, WritableSignal } from '@angular/core';
import 'chartjs-adapter-date-fns';
import { SelectModule } from 'primeng/select';
import { FormsModule } from '@angular/forms';
import { DataPoint, MultiLineChartWidget } from '../../models/dashboard';
import { de } from 'date-fns/locale';
import { addDays } from 'date-fns';

@Component({
  selector: 'app-multi-line-chart-widget',
  imports: [CommonModule, ChartModule, SelectModule, FormsModule],
  providers: [DatePipe],
  standalone: true,
  templateUrl: './multi-line-chart-widget.component.html',
  styleUrl: './multi-line-chart-widget.component.css'
})
export class MultiLineChartWidgetComponent {

    todayString!: string;
    threeMonthsAgoString!: string;

  class = input<string>('');
  widgetHeader: WritableSignal<string> = signal('');

  labels: string[] = [];

  datasets: any[] = [];

  bgColors: (string | string[])[] | undefined | null | any;

  borderColors:(string | string[])[] | undefined | null | any;

  show: number = 12;

  tooltipPrefix: string = '€';

  option:  WritableSignal<string> = signal('month');
  chartRanges:{ name: string, unit: string }[] = [];
  selectedRange: string = 'month';

  stepSize: number = 2000;

  dataReduction: boolean = true;

  data: any;

  plugins: any;

  chartData: any;

  chartOptions: any;

  constructor(@Inject('contentData') public contentData: any, @Inject('label') public label: any, private datePipe: DatePipe) {
    // Heute um 00:00
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // 3 Monate zurück um 00:00
    const threeMonthsAgo = new Date(today);
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

    // Format anwenden
    this.todayString = this.datePipe.transform(today, "yyyy-MM-dd'T'HH:mm:ss") ?? '';
    this.threeMonthsAgoString = this.datePipe.transform(threeMonthsAgo, "yyyy-MM-dd'T'HH:mm:ss")?? '';
  
    this.data = signal<MultiLineChartWidget>(JSON.parse(this.contentData));
    this.widgetHeader.set(this.label);
    this.data();
    this.labels = this.data().labels || [];
    this.bgColors = this.data().bgColors || [];
    this.borderColors = this.data().borderColors || [];
    this.chartRanges = this.data().chartRanges || [];
    this.selectedRange = this.data().defaultChartRange?.unit || 'week';

    this.datasets = generateRandomMultiData(this.threeMonthsAgoString, today, 24, 2000, 6000, 2, true);
      effect(() => {         
          
          this.option();
          this.drawChart();

          //untracked(() => this.tooltipPrefix());
      });
  }

  drawChart() {
    switch(this.option()) {
      case 'day':
        this.show = 7;
        break;
        case 'week':
            this.show = 4;
        break;
        case 'month':
            this.show = 3;
        break;
    }

      this.chartData = this.setChartData(this.option());
      this.chartOptions = this.setChartOptions(this.tooltipPrefix);
      this.plugins = this.setChartPlugins();
  }

  setChartData(option: string) {
      const sampledData = this.dataReduction ? sampleDataReductionByArray(this.datasets, option, this.show) : this.datasets;
      if (sampledData.length <= 0) {
          this.data = [];

          return {
              datasets: null
          };
      }
      const darkMode =  false;
      const rootStyles = getComputedStyle(document.documentElement);
      const surface0Color = rootStyles.getPropertyValue('--p-surface-0');
      const surface950Color = rootStyles.getPropertyValue('--p-surface-950');

      const lineCount = sampledData[0].y.length;
      const dataArr: any = Array(lineCount)
          .fill(null)
          .map((_, index) => {
              return {
                  label: this.labels[index] ?? 'dataset' + index,
                  data: [],
                  fill: true,
                  borderColor: this.borderColors?.[index] ?? (darkMode ? '#FAFAFA' : '#030616'),
                  tension: 0.3,
                  borderWidth: 1.2,
                  pointBorderColor: 'rgba(0, 0, 0, 0)',
                  pointBackgroundColor: 'rgba(0, 0, 0, 0)',
                  pointHoverBackgroundColor: this.borderColors?.[index] ?? (darkMode ? surface0Color : surface950Color),
                  pointHoverBorderColor: darkMode ? surface950Color : surface0Color,
                  pointBorderWidth: 12,
                  hideInLegendAndTooltip: false,
                  pointStyle: 'circle',
                  pointRadius: 4,
                  backgroundColor: (context: any) => {
                      const defaultColor = [darkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(3, 6, 22, 0.06)', darkMode ? 'rgba(255, 255, 255, 0)' : 'rgba(3, 6, 22, 0)'];
                      const bg = this.bgColors?.[index] !== undefined ? this.bgColors![index] : defaultColor;

                      if (!context.chart.chartArea) {
                          return;
                      }

                      const {
                          ctx,
                          chartArea: { top, bottom }
                      } = context.chart;
                      const gradientBg = ctx.createLinearGradient(0, top, 0, bottom);
                      const colorTranches = 1 / (bg.length - 1);

                      for (let i = 0; i < bg.length; i++) {
                          gradientBg.addColorStop(0 + i * colorTranches, bg[i]);
                      }

                      return gradientBg;
                  }
              };
          });
      sampledData.forEach(({ x, y }) => {
          Array(lineCount)
              .fill(null)
              .forEach((_, i) => {
                  dataArr[i].data.push({ x, y: y[i] });
              });
      });
      this.data = dataArr;
      return {
          datasets: dataArr
      };
  }

  setChartPlugins() {
      const hoverLine = {
          id: 'hoverLine',
          beforeDatasetsDraw: (chart: any) => {
              const {
                  ctx,
                  tooltip,
                  chartArea: { bottom },
                  scales: { x }
              } = chart;
              if (tooltip && tooltip._active.length > 0) {
                  const xCoor = x.getPixelForValue(tooltip.dataPoints[0].raw.x);
                  ctx.save();
                  ctx.beginPath();
                  ctx.lineWidth = 1.2;
                  const rootStyles = getComputedStyle(document.documentElement);
                  const surface500Color = rootStyles.getPropertyValue('--p-surface-500');
                  ctx.strokeStyle = surface500Color;
                  ctx.setLineDash([4, 4]);
                  ctx.moveTo(xCoor, 0);
                  ctx.lineTo(xCoor, bottom + 8);
                  ctx.stroke();
                  ctx.closePath();
                  ctx.restore();
              }
          }
      };

      return [hoverLine];
  }

  setChartOptions(tooltipPrefix: string) {
      const darkMode = false;
      const rootStyles = getComputedStyle(document.documentElement);
      const surface400Color = rootStyles.getPropertyValue('--p-surface-400');
      const surface200Color = rootStyles.getPropertyValue('--p-surface-200');

      const endDate = new Date(this.data[this.data.length - 1].x);
      const startDate = new Date(this.data[0].x);
      return {
          interaction: {
              intersect: false,
              mode: 'index'
          },
          responsive: true,
          maintainAspectRatio: false,
          layout: {
              padding: 0
          },
          plugins: {
              tooltip: {
                  enabled: false,
                  position: 'nearest',
                  external: function (context: any) {
                      const { chart, tooltip } = context;
                      let tooltipEl = chart.canvas.parentNode.querySelector('div.chartjs-tooltip');
                      if (!tooltipEl) {
                          tooltipEl = document.createElement('div');
                          tooltipEl.classList.add(
                              'chartjs-tooltip',
                              'dark:bg-surface-950',
                              'bg-white',
                              'rounded-[8px]',
                              'overflow-hidden',
                              'opacity-100',
                              'border',
                              'border-surface',
                              'absolute',
                              'transition-all',
                              'duration-[0.05s]',
                              'pointer-events-none',
                              'shadow-[0px_16px_32px_-12px_rgba(88,92,95,0.10)]'
                          );
                          chart.canvas.parentNode.appendChild(tooltipEl);
                      }

                      if (tooltip.opacity === 0) {
                          tooltipEl.style.opacity = 0;
                          return;
                      }
                      const datasetPointsX = tooltip.dataPoints.map((dp: any) => dp.element.x);
                      const avgX = datasetPointsX.reduce((a: any, b: any) => a + b, 0) / datasetPointsX.length;

                      const datasetPointsY = tooltip.dataPoints.map((dp: any) => dp.element.y);
                      const avgY = datasetPointsY.reduce((a: any, b: any) => a + b, 0) / datasetPointsY.length;

                      if (tooltip.body) {
                          tooltipEl.innerHTML = '';
                          const tooltipHeader = document.createElement('div');
                          tooltipHeader.classList.add('bg-gray-100', 'dark:bg-surface-900', 'px-3', 'py-2.5', 'border-b', 'border-surface', 'text-left', 'label-xsmall');
                          tooltipHeader.appendChild(document.createTextNode(tooltip.title[0]));
                          tooltipEl.appendChild(tooltipHeader);
                          const tooltipBody = document.createElement('div');
                          tooltipBody.classList.add('flex', 'flex-col', 'gap-2', 'px-3', 'py-2', 'min-w-[12.5rem]');
                          tooltip.dataPoints.forEach((body: any) => {
                              const row = document.createElement('div');
                              row.classList.add('flex', 'items-center', 'gap-2', 'w-full');
                              const point = document.createElement('div');
                              point.classList.add('w-2.5', 'h-2.5', 'rounded-full');
                              point.style.backgroundColor = body.dataset.borderColor;
                              row.appendChild(point);
                              const label = document.createElement('span');
                              label.appendChild(document.createTextNode(body.dataset.label));
                              label.classList.add('text-base', 'text-surface-950', 'dark:text-surface-0', 'flex-1', 'text-left', 'capitalize');
                              row.appendChild(label);
                              const value = document.createElement('span');
                              value.appendChild(document.createTextNode(tooltipPrefix + body.formattedValue));
                              value.classList.add('text-base', 'text-surface-950', 'dark:text-surface-0', 'text-right');
                              row.appendChild(value);
                              tooltipBody.appendChild(row);
                          });
                          tooltipEl.appendChild(tooltipBody);
                      }

                      const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

                      tooltipEl.style.opacity = 1;
                      tooltipEl.style.font = tooltip.options.bodyFont.string;
                      tooltipEl.style.padding = '0px';

                      const chartWidth = chart.width;
                      const tooltipWidth = tooltipEl.offsetWidth;
                      const chartHeight = chart.height;
                      const tooltipHeight = tooltipEl.offsetHeight;

                      let tooltipX = positionX + avgX + 20;
                      let tooltipY = positionY + avgY - tooltipHeight / 2;

                      if (tooltipX + tooltipWidth > chartWidth) {
                          tooltipX = positionX + avgX - tooltipWidth - 20;
                      }

                      if (tooltipY < 0) {
                          tooltipY = 0;
                      } else if (tooltipY + tooltipHeight > chartHeight) {
                          tooltipY = chartHeight - tooltipHeight;
                      }

                      tooltipEl.style.left = tooltipX + 'px';
                      tooltipEl.style.top = tooltipY + 'px';
                  }
              },
              legend: {
                  display: false
              },
              title: {
                  display: false
              }
          },
          scales: {
              x: {
                  type: 'time',
                  time: {
                      unit: this.option(),
                      tooltipFormat: 'MM/dd/yyyy HH:mm',
                      displayFormats: {
                        day: 'EEEE, dd.MM.yyyy', 
                        week: "'KW' II/yyyy",
                        month: 'MMMM',
                      },
                  },
                  adapters: {
                    date: {
                      locale: de                // damit die Wochentage auf Deutsch kommen
                    }
                  },
                  min: startDate,
                  max: endDate,
                  offset: false,
                  bounds: 'data',
                  round: false, 
                  grid: {
                      display: true,
                      lineWidth: 1.2,
                      color: 'rgba(255,255,255,0)'
                  },
                  ticks: {
                      color: darkMode ? 'rgba(255, 255, 255, 0.40)' : surface400Color,
                      autoSkip: false,
                      maxRotation: 0,
                      source: 'auto'
                  },
                  border: {
                      display: false
                  }
                  
              },
              y: {
                  beginAtZero: true,
                  display: true,
                  min: 0,
                  border: {
                      display: false
                  },
                  ticks: {
                      color: darkMode ? 'rgba(255, 255, 255, 0.40)' : surface400Color,
                      autoSkip: false,
                      maxRotation: 0,
                      source: 'auto',
                      stepSize: this.stepSize,
                      callback: function (value: any) {
                          if (value > 1000) {
                              return value / 1000 + 'K';
                          }
                          return value;
                      }
                  },
                  grid: {
                      display: true,
                      lineWidth: 1,
                      color: darkMode ? 'rgba(255, 255, 255, 0.08)' : surface200Color
                  }
              }
          }
      };
  }
}


export type TimeUnit = 'millisecond' | 'second' | 'minute' | 'hour' | 'day' | 'week' | 'month' | 'quarter' | 'year' | string;

export const parseDate = (dateStr: string | Date): Date => {
  return typeof dateStr === 'string' ? new Date(dateStr) : dateStr;
};

export const sampleDataReductionByArray = (data: DataPoint[], option: TimeUnit, show: number): DataPoint[] => {
  let sampledData: DataPoint[] = [];
  const dataLength: number = data.length;

  if (dataLength === 0) return sampledData;

  const timeUnits: Record<TimeUnit, number> = {
      millisecond: 1,
      second: 1000,
      minute: 60 * 1000,
      hour: 60 * 60 * 1000,
      day: 24 * 60 * 60 * 1000,
      week: 7 * 24 * 60 * 60 * 1000,
      month: 30 * 24 * 60 * 60 * 1000,
      quarter: 90 * 24 * 60 * 60 * 1000,
      year: 365 * 24 * 60 * 60 * 1000
  };

  const selectedMultiplier: number = timeUnits[option];
  const endDate: Date = parseDate(data[dataLength - 1].x);
  const firstData: Date = parseDate(data[0].x);
  const calculatedDate: Date = new Date(endDate.getTime() - show * selectedMultiplier);
  const startDate: Date = firstData.getTime() > calculatedDate.getTime() ? firstData : calculatedDate;

  let tempData: DataPoint[] = data.filter((item) => parseDate(item.x) >= startDate);
  const tempLen: number = tempData.length;

  if (['minute', 'second', 'millisecond'].includes(option)) {
      if (tempLen < show) {
          const adjustedStartDate: Date = new Date(endDate);
          adjustedStartDate.setHours(endDate.getHours() - 4);
          tempData = data.filter((item) => parseDate(item.x) >= adjustedStartDate);
      }
      return tempData;
  } else if (['day', 'hour'].includes(option)) {
      return tempData;
  } else if (['week', 'month', 'quarter', 'year'].includes(option)) {
      const sliceUnits: Record<'week' | 'month' | 'quarter' | 'year', number> = {
          week: 1, // Alle 7 Tage ein Datenpunkt (7)
          month: 1,
          quarter: 90,
          year: 366 / 2
      };

      // Check if the `option` is present in `sliceUnits`
      if (option in sliceUnits) {
          const selectedSlicer: number = sliceUnits[option as keyof typeof sliceUnits];
          for (let i = 0; i < tempLen; i += selectedSlicer) {
              let sampledPointY: number | number[];

              // Handle case where the `y` value is an array
              if (Array.isArray(tempData[i].y)) {
                  sampledPointY = [...(tempData[i].y as number[])]; // Copy array to avoid mutation
              } else {
                  sampledPointY = tempData[i].y as number;
              }

              sampledData.push({ x: tempData[i].x, y: sampledPointY });
          }
      }
      return sampledData;
  }

  return data;
};

export const generateRandomMultiData = (startDate: string | Date, endDate: string | Date, intervalHours: number, minValue: number, maxValue: number, datasetsCount: number = 2, inter: boolean = false): DataPoint[] => {
    const data: DataPoint[] = [];
    let currentDate: Date = parseDate(startDate);
    const end: Date = parseDate(endDate);

    while (currentDate <= end) {
        let currentValues;
        if (inter) {
            let incr = maxValue;
            currentValues = Array(datasetsCount)
                .fill(null)
                .map((_, i) => {
                    return (minValue + Math.random() * (maxValue - minValue) + incr * (datasetsCount - i * 1.2)).toFixed(0);
                });
        } else {
            currentValues = Array(datasetsCount)
                .fill(null)
                .map(() => {
                    return (minValue + Math.random() * (maxValue - minValue)).toFixed(0);
                });
        }
        data.push({ x: new Date(currentDate), y: [...currentValues] });

        currentDate = new Date(currentDate.getTime() + intervalHours * 60 * 60 * 1000);
    }


    
    return data;
};
